import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Rocket, CheckSquare, Play, X, Sparkles, Users, Calendar } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useOnboardingStore } from '../stores/onboardingStore'
import api from '../lib/api'
import toast from 'react-hot-toast'

interface WelcomeModalProps {
  onStartTour: () => void;
  onClose: () => void;
}

export function WelcomeModal({ onStartTour, onClose }: WelcomeModalProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { markWelcomeSeen, markDemoDataSeeded } = useOnboardingStore()
  const [isSeeding, setIsSeeding] = useState(false)

  const handleStartTour = async () => {
    await markWelcomeSeen()
    onClose()
    onStartTour()
  }

  const handleGoToChecklist = async () => {
    await markWelcomeSeen()
    onClose()
    navigate('/getting-started')
  }

  const handleSeedDemoData = async () => {
    setIsSeeding(true)
    try {
      const { data } = await api.post('/demo/seed-data')
      await markWelcomeSeen()
      await markDemoDataSeeded()
      toast.success(data.message)
      onClose()
      navigate('/')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error creando datos de ejemplo')
    } finally {
      setIsSeeding(false)
    }
  }

  const handleSkip = async () => {
    await markWelcomeSeen()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-[480px] rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary-500 to-primary-700 px-8 pt-10 pb-8 text-center">
          <button
            onClick={handleSkip}
            className="absolute right-4 top-4 rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>

          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">
            ¡Bienvenido{user?.firstName ? `, ${user.firstName}` : ''}!
          </h2>
          <p className="mt-2 text-sm text-white/80">
            Tu clínica está lista. Elige cómo quieres empezar a explorar SmartDental.
          </p>
        </div>

        {/* Options */}
        <div className="px-6 py-6 space-y-3">
          <button
            onClick={handleStartTour}
            className="group flex w-full items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-left transition-all hover:border-primary-200 hover:bg-primary-50"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-200">
              <Rocket className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Tour rápido</p>
              <p className="text-xs text-slate-500">2 minutos por las funciones principales</p>
            </div>
            <Play className="h-4 w-4 text-slate-300 transition-colors group-hover:text-primary-500" />
          </button>

          <button
            onClick={handleGoToChecklist}
            className="group flex w-full items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-left transition-all hover:border-emerald-200 hover:bg-emerald-50"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 transition-colors group-hover:bg-emerald-200">
              <CheckSquare className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Checklist de inicio</p>
              <p className="text-xs text-slate-500">Completa 5 pasos para configurar tu clínica</p>
            </div>
          </button>

          <button
            onClick={handleSeedDemoData}
            disabled={isSeeding}
            className="group flex w-full items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-left transition-all hover:border-amber-200 hover:bg-amber-50 disabled:opacity-50"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 transition-colors group-hover:bg-amber-200">
              <Users className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">
                {isSeeding ? 'Creando datos...' : 'Ver con datos de ejemplo'}
              </p>
              <p className="text-xs text-slate-500">
                Explora la app con pacientes y citas ficticios
              </p>
            </div>
            {isSeeding ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-300 border-t-amber-600" />
            ) : (
              <Calendar className="h-4 w-4 text-slate-300 transition-colors group-hover:text-amber-500" />
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4">
          <button
            onClick={handleSkip}
            className="w-full text-center text-xs font-medium text-slate-400 transition-colors hover:text-slate-600"
          >
            Saltar por ahora, lo haré más tarde
          </button>
        </div>
      </div>
    </div>
  )
}
